import { zodResolver } from '@hookform/resolvers/zod';
import { TextField } from '@mui/material';
import type { StripeAuthConnectRequestPayload } from '@weft/api-definition';
import { StripeAuthConnectRequestSchema } from '@weft/api-definition';
import { isNotNullOrUndefined } from '@weft/utils';
import VyDialog from 'apps/app/src/components/new-design/feedback/VyDialog';
import { useStripeConnect } from 'apps/app/src/hooks/requests/use-stripe-connect';
import { useDialog } from 'apps/app/src/hooks/use-dialog';
import { runMutation } from 'apps/app/src/utils/vy-mutation';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';

export type ConnectStripeModalProps = {
  open: boolean;
  onClose: () => void;
};  

export const ConnectStripeModal = ({ open, onClose }: ConnectStripeModalProps): React.ReactElement => {
  const { isDialogOpen, handleOpen, handleClose } = useDialog();
  const form = useForm<StripeAuthConnectRequestPayload>({
    resolver: zodResolver(StripeAuthConnectRequestSchema),
  });

  const apiTokenInputRegister = form.register('apiToken');

  const closeDialog = () => {
    handleClose();
    onClose();
  };
  
  const mutateAsyncStripeConnect = useStripeConnect();

  const handleSubmit = form.handleSubmit(async ({ apiToken }) => {
    await runMutation(
      mutateAsyncStripeConnect,
      { apiToken },
      {
        pending: 'Connecting...',
        success: 'Connected',
        error: 'Failed to connect',
      },
      {
        onSuccess: closeDialog,
        onError: () => {
          form.setError('apiToken', {
            type: 'manual',
            message: 'Invalid API token',
          });
        },
      });
  });

  useEffect(() => {
    if (open) {
      handleOpen();
    }
  }
  , [open, handleOpen]);

  return (
    <VyDialog
      component="form"
      open={isDialogOpen}
      onClose={closeDialog}
      label="Connect Stripe account"
      ctaText="Connect"
      cancelButtonText="Cancel"
      onSubmit={handleSubmit}
      hideBackdrop
    >
      <TextField 
        fullWidth
        label="Enter Stripe API token"
        variant="filled"
        size='medium'
        sx={{ padding: '10px' }}
        error={isNotNullOrUndefined(form.formState.errors.apiToken)}
        helperText={form.formState.errors.apiToken?.message}
        {...apiTokenInputRegister}
      />
    </VyDialog>
  );
};
